import { useQueries } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Batch } from '../../types/sharedTypeImpl.ts';
import { trpcClient } from '../../utils/trpc/trpc.ts';
import { EquipmentOption, SelectedPart } from './types.ts';

interface Props {
    batches: Batch[] | undefined;
    selectedBatch: Batch | null;
    selectedEquipment: EquipmentOption | null;
    selectedPart: SelectedPart | null;
}


export function useFetchBatchKpis({
    batches,
    selectedBatch,
    selectedEquipment,
    selectedPart
}: Props) {
    const abortController = useRef(new AbortController());

    const batchQueriesParams = batches?.map(batch => ({
        equipment: batch.equipment,
        product: batch.product,
        start: batch.start,
        end: batch.end ?? null,
    }));

    // Cancel kpi requests, when the list of batches is about to change.
    // This is required because browsers have a limit on the number of concurrent requests and
    // the queries may take a long time to complete, if the KPIs are not cached.
    // We also cannot wait for the list of batches to actually change, because it is depdendent
    // on further requests, which cannot start until the current requests are finished.
    useEffect(() => {
        return () => {
            abortController.current.abort();
            abortController.current = new AbortController();
        }
    }, [batches, selectedEquipment, selectedPart?.partName, selectedPart?.product]);

    //Fetch kpis for each batch
    //Use vanilla useQueries, as we need the 'signal' field
    const kpiQueries = useQueries({
        queries: batchQueriesParams?.map(params => ({
            queryKey: ['machine-log-kpis', ...Object.values(params)],
            queryFn: () => trpcClient.machineLogKpis.get.query(params),
            refetchOnWindowFocus: false,
            signal: abortController.current.signal,
        })) ?? [],
    });

    function refetchCurrentBatchKpis() {
        if (!batches || !selectedBatch) return;
        const index = batches.findIndex(it => it === selectedBatch);
        kpiQueries[index].refetch();
    }

    return {
        kpiQueries,
        refetchCurrentBatchKpis,
    };
}
